import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  TrendingUp, CloudSun, ShoppingCart, Sparkles, RefreshCw, CheckCircle2,
  AlertCircle, ArrowUpRight, Sun, CloudRain, Coffee, Check, BarChart3,
  Layers, Zap, ShieldCheck
} from "lucide-react";

export default function InteractiveShowcase() {
  const [activeTab, setActiveTab] = useState("forecast");
  const [tempAdjust, setTempAdjust] = useState(21);
  const [isRainy, setIsRainy] = useState(false);
  const [dayType, setDayType] = useState("Friday");
  const [poStatus, setPoStatus] = useState("ready"); // ready | generating | ordered

  // Calculate dynamic forecast numbers based on interactive sliders
  const tempDiff = tempAdjust - 18;
  const hotMultiplier = Math.max(0.7, 1 - tempDiff * 0.03 - (isRainy ? -0.08 : 0));
  const coldMultiplier = Math.max(0.6, 1 + tempDiff * 0.05 - (isRainy ? 0.2 : 0));
  const dayMultiplier = dayType === "Saturday" ? 1.35 : dayType === "Friday" ? 1.15 : 1.0;

  const predictedFlatWhites = Math.round(145 * hotMultiplier * dayMultiplier);
  const predictedIcedLattes = Math.round(48 * coldMultiplier * dayMultiplier);
  const predictedPastries = Math.round(85 * (isRainy ? 1.1 : 0.95) * dayMultiplier);
  const predictedRevenue = Math.round(
    predictedFlatWhites * 5.5 + predictedIcedLattes * 6.5 + predictedPastries * 5.8
  );

  const milkRequired = (predictedFlatWhites * 0.22 + predictedIcedLattes * 0.15).toFixed(1);
  const beansRequired = (
    (predictedFlatWhites * 18 + predictedIcedLattes * 18) /
    1000
  ).toFixed(1);

  const handleGeneratePo = () => {
    setPoStatus("generating");
    setTimeout(() => {
      setPoStatus("ordered");
      setTimeout(() => setPoStatus("ready"), 4000);
    }, 900);
  };

  const tabs = [
    { id: "forecast", label: "Demand Forecaster", icon: TrendingUp },
    { id: "weather", label: "Weather Intelligence", icon: CloudSun },
    { id: "purchasing", label: "Smart PO Generator", icon: ShoppingCart },
    { id: "copilot", label: "AI Operations Briefing", icon: Sparkles },
    { id: "pos", label: "Real-Time POS Sync", icon: Zap },
  ];

  return (
    <div className="w-full bg-zinc-950 border border-zinc-800/80 rounded-2xl p-4 md:p-6 shadow-2xl text-left overflow-hidden">
      {/* Top Header Bar with OS-like indicators */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-zinc-800/80">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 mr-2">
            <div className="h-3 w-3 rounded-full bg-red-500/80" />
            <div className="h-3 w-3 rounded-full bg-amber-500/80" />
            <div className="h-3 w-3 rounded-full bg-emerald-500/80" />
          </div>
          <span className="text-xs font-mono font-medium text-zinc-400">
            optics://live-demo/christchurch-roastery
          </span>
          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-400 bg-emerald-950/60 border border-emerald-800/50 px-2 py-0.5 rounded-full ml-2">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Live Engine
          </span>
        </div>
        <div className="text-xs text-zinc-400 font-mono hidden sm:block">
          MAPE: <span className="text-emerald-400 font-semibold">tracked daily</span>
        </div>
      </div>

      {/* Mode Switcher Tabs */}
      <div className="flex items-center gap-1.5 overflow-x-auto py-3 no-scrollbar">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all duration-200 ${
              activeTab === tab.id
                ? "bg-zinc-800 text-white shadow-sm border border-zinc-700"
                : "text-zinc-400 hover:text-zinc-200 hover:bg-zinc-900"
            }`}
          >
            <tab.icon className={`h-3.5 w-3.5 ${activeTab === tab.id ? "text-amber-400" : ""}`} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Main Tab Content Display */}
      <div className="pt-3 min-h-[340px]">
        <AnimatePresence mode="wait">
          {/* TAB 1: DEMAND FORECASTER */}
          {activeTab === "forecast" && (
            <motion.div
              key="forecast"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className="space-y-4"
            >
              {/* Interactive Control Strip */}
              <div className="bg-zinc-900/90 border border-zinc-800 rounded-xl p-3.5 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-semibold uppercase tracking-wider text-zinc-400">
                    Live Simulation:
                  </span>
                  <div className="flex items-center gap-1 bg-zinc-950 p-1 rounded-lg border border-zinc-800">
                    {["Thursday", "Friday", "Saturday"].map((d) => (
                      <button
                        key={d}
                        onClick={() => setDayType(d)}
                        className={`text-xs px-2.5 py-1 rounded-md transition-colors ${
                          dayType === d
                            ? "bg-zinc-800 text-white font-medium shadow-sm"
                            : "text-zinc-400 hover:text-zinc-200"
                        }`}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Sun className="h-3.5 w-3.5 text-amber-400" />
                    <span className="text-xs text-zinc-300 tnum">{tempAdjust}°C</span>
                    <input
                      type="range"
                      min="8"
                      max="32"
                      value={tempAdjust}
                      onChange={(e) => setTempAdjust(Number(e.target.value))}
                      className="w-20 accent-amber-500 cursor-pointer h-1.5 bg-zinc-800 rounded-lg"
                    />
                  </div>

                  <button
                    onClick={() => setIsRainy(!isRainy)}
                    className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md border transition-colors ${
                      isRainy
                        ? "bg-sky-950/80 border-sky-700 text-sky-300"
                        : "bg-zinc-950 border-zinc-800 text-zinc-400 hover:text-zinc-200"
                    }`}
                  >
                    <CloudRain className="h-3.5 w-3.5 text-sky-400" />
                    {isRainy ? "Rain Forecasted" : "No Rain"}
                  </button>
                </div>
              </div>

              {/* KPI Metrics */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-zinc-900/60 border border-zinc-800/80 rounded-xl p-3.5">
                  <div className="text-[11px] uppercase tracking-wider text-zinc-400">Forecast Revenue</div>
                  <div className="text-2xl font-bold font-display text-white mt-1 tnum">
                    ${predictedRevenue.toLocaleString()}
                  </div>
                  <div className="text-[11px] text-emerald-400 mt-1 flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" /> +{(dayMultiplier * 100 - 100).toFixed(0)}% vs normal
                  </div>
                </div>

                <div className="bg-zinc-900/60 border border-zinc-800/80 rounded-xl p-3.5">
                  <div className="text-[11px] uppercase tracking-wider text-zinc-400">Flat Whites / Lattes</div>
                  <div className="text-2xl font-bold font-display text-amber-400 mt-1 tnum">
                    {predictedFlatWhites} <span className="text-xs font-normal text-zinc-400">cups</span>
                  </div>
                  <div className="text-[11px] text-zinc-400 mt-1">
                    {hotMultiplier > 1 ? "Cold weather surge" : "Mild demand"}
                  </div>
                </div>

                <div className="bg-zinc-900/60 border border-zinc-800/80 rounded-xl p-3.5">
                  <div className="text-[11px] uppercase tracking-wider text-zinc-400">Iced / Cold Brew</div>
                  <div className="text-2xl font-bold font-display text-sky-400 mt-1 tnum">
                    {predictedIcedLattes} <span className="text-xs font-normal text-zinc-400">cups</span>
                  </div>
                  <div className="text-[11px] text-zinc-400 mt-1">
                    {tempAdjust >= 22 ? "+38% warm surge" : "Standard baseline"}
                  </div>
                </div>

                <div className="bg-zinc-900/60 border border-zinc-800/80 rounded-xl p-3.5">
                  <div className="text-[11px] uppercase tracking-wider text-zinc-400">Prep: Fresh Milk</div>
                  <div className="text-2xl font-bold font-display text-white mt-1 tnum">
                    {milkRequired} <span className="text-xs font-normal text-zinc-400">L</span>
                  </div>
                  <div className="text-[11px] text-emerald-400 mt-1">Zero stockout guarantee</div>
                </div>
              </div>

              {/* Live Forecast Distribution Bar */}
              <div className="bg-zinc-900/40 border border-zinc-800/80 rounded-xl p-4">
                <div className="flex items-center justify-between text-xs text-zinc-400 mb-2">
                  <span>Product Demand Breakdown</span>
                  <span className="text-zinc-500">Beans needed: {beansRequired} kg</span>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-zinc-300 w-28 truncate">Hot Espresso</span>
                    <div className="flex-1 h-3 bg-zinc-800 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-amber-500 rounded-full"
                        animate={{ width: `${Math.min(100, (predictedFlatWhites / 200) * 100)}%` }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                    <span className="text-xs text-zinc-300 w-12 text-right tnum">{predictedFlatWhites}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-zinc-300 w-28 truncate">Cold Drinks</span>
                    <div className="flex-1 h-3 bg-zinc-800 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-sky-500 rounded-full"
                        animate={{ width: `${Math.min(100, (predictedIcedLattes / 100) * 100)}%` }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                    <span className="text-xs text-zinc-300 w-12 text-right tnum">{predictedIcedLattes}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-zinc-300 w-28 truncate">Fresh Bakery</span>
                    <div className="flex-1 h-3 bg-zinc-800 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-emerald-500 rounded-full"
                        animate={{ width: `${Math.min(100, (predictedPastries / 120) * 100)}%` }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                    <span className="text-xs text-zinc-300 w-12 text-right tnum">{predictedPastries}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* TAB 2: WEATHER INTELLIGENCE */}
          {activeTab === "weather" && (
            <motion.div
              key="weather"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { day: "Mon", temp: "16°C", rain: "80%", hot: "+12%", cold: "-18%", cond: "Rainy" },
                  { day: "Tue", temp: "19°C", rain: "10%", hot: "+2%", cold: "+4%", cond: "Clear" },
                  { day: "Wed", temp: "24°C", rain: "0%", hot: "-14%", cold: "+42%", cond: "Sunny" },
                  { day: "Thu (Peak)", temp: "26°C", rain: "0%", hot: "-22%", cold: "+65%", cond: "Hot" },
                ].map((w, idx) => (
                  <div
                    key={w.day}
                    className={`p-3.5 rounded-xl border ${
                      idx === 3
                        ? "bg-amber-950/30 border-amber-800/80 ring-1 ring-amber-500/40"
                        : "bg-zinc-900/60 border-zinc-800/80"
                    }`}
                  >
                    <div className="flex items-center justify-between text-xs text-zinc-400">
                      <span className="font-semibold text-zinc-200">{w.day}</span>
                      <span className="text-zinc-500">{w.cond}</span>
                    </div>
                    <div className="text-xl font-bold text-white mt-1">{w.temp}</div>
                    <div className="mt-2 text-xs space-y-1">
                      <div className="text-amber-400">Hot drinks: {w.hot}</div>
                      <div className="text-sky-400">Cold drinks: {w.cold}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-xs text-zinc-300">
                <div className="font-semibold text-amber-400 mb-1 flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5" /> Automated Weather Opportunity Detected:
                </div>
                Thursday will experience a +8°C heat spike with 0% rain. Optics automatically pre-allocates
                +65% cold brew & iced latte batches and reduces hot croissant bake volume by 12% to prevent afternoon food waste.
              </div>
            </motion.div>
          )}

          {/* TAB 3: SMART PURCHASING */}
          {activeTab === "purchasing" && (
            <motion.div
              key="purchasing"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="space-y-4"
            >
              <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl overflow-hidden text-xs">
                <table className="w-full">
                  <thead>
                    <tr className="text-zinc-400 border-b border-zinc-800 bg-zinc-950/60">
                      <th className="px-4 py-2.5 text-left">Item</th>
                      <th className="px-4 py-2.5 text-right">In Stock</th>
                      <th className="px-4 py-2.5 text-right">Forecast Need</th>
                      <th className="px-4 py-2.5 text-right">Order Rec</th>
                      <th className="px-4 py-2.5 text-left">Supplier</th>
                      <th className="px-4 py-2.5 text-right">Est. Cost</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800/60">
                    <tr>
                      <td className="px-4 py-2.5 font-medium text-white flex items-center gap-1.5">
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> Espresso Beans (House Blend)
                      </td>
                      <td className="px-4 py-2.5 text-right text-red-400 tnum font-semibold">4.5 kg (Low)</td>
                      <td className="px-4 py-2.5 text-right text-zinc-300 tnum">12.8 kg</td>
                      <td className="px-4 py-2.5 text-right text-emerald-400 font-bold tnum">+15.0 kg</td>
                      <td className="px-4 py-2.5 text-zinc-400">Roastery Co (2d lead)</td>
                      <td className="px-4 py-2.5 text-right text-zinc-200 tnum font-medium">$630.00</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2.5 font-medium text-white flex items-center gap-1.5">
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> Fresh Whole Milk (2L)
                      </td>
                      <td className="px-4 py-2.5 text-right text-zinc-400 tnum">18.0 L</td>
                      <td className="px-4 py-2.5 text-right text-zinc-300 tnum">46.0 L</td>
                      <td className="px-4 py-2.5 text-right text-emerald-400 font-bold tnum">+35.0 L</td>
                      <td className="px-4 py-2.5 text-zinc-400">Canterbury Dairy (1d lead)</td>
                      <td className="px-4 py-2.5 text-right text-zinc-200 tnum font-medium">$63.00</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2.5 font-medium text-white flex items-center gap-1.5">
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> Oat Milk Barista Edition
                      </td>
                      <td className="px-4 py-2.5 text-right text-amber-400 tnum font-semibold">8.0 L (Warning)</td>
                      <td className="px-4 py-2.5 text-right text-zinc-300 tnum">24.0 L</td>
                      <td className="px-4 py-2.5 text-right text-emerald-400 font-bold tnum">+20.0 L</td>
                      <td className="px-4 py-2.5 text-zinc-400">EcoPack Supplies (3d lead)</td>
                      <td className="px-4 py-2.5 text-right text-zinc-200 tnum font-medium">$72.00</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between pt-1">
                <span className="text-xs text-zinc-400">
                  Total Order Est: <strong className="text-white text-sm">$765.00 NZD</strong> (3 Suppliers)
                </span>
                <button
                  onClick={handleGeneratePo}
                  disabled={poStatus === "generating"}
                  className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-xl text-xs font-semibold transition-colors duration-200 shadow-lg shadow-emerald-950"
                >
                  {poStatus === "generating" ? (
                    <>
                      <RefreshCw className="h-3.5 w-3.5 animate-spin" /> Generating POs…
                    </>
                  ) : poStatus === "ordered" ? (
                    <>
                      <Check className="h-3.5 w-3.5" /> 3 Purchase Orders Dispatched!
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="h-3.5 w-3.5" /> 1-Click Generate & Send POs
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          )}

          {/* TAB 4: AI OPERATIONS COPILOT */}
          {activeTab === "copilot" && (
            <motion.div
              key="copilot"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="space-y-3"
            >
              {[
                {
                  title: "Morning Barista Briefing",
                  body: "Prepare +35% cold brew concentrate by 7:30 AM. Tomorrow's 24°C temperature spike coincides with the Friday university rush. Allocate second grinder to single-origin cold drip.",
                  type: "Staffing & Prep",
                  badge: "High Impact",
                },
                {
                  title: "Bakery Waste Reduction",
                  body: "Forecast-led baking reduces overproduction on slow mornings — bake closer to predicted demand instead of guessing.",
                  type: "Waste Prevention",
                  badge: "Saves $84/wk",
                },
                {
                  title: "Milk Expiry Rebalancing",
                  body: "Use Batch #409 (expires in 48h) first for morning flat white rush to maintain zero dairy spoilage.",
                  type: "Inventory",
                  badge: "Optimized",
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="bg-zinc-900/70 border border-zinc-800/90 rounded-xl p-3.5 flex gap-3 items-start"
                >
                  <div className="h-7 w-7 rounded-lg bg-amber-500/10 border border-amber-500/20 grid place-items-center shrink-0 mt-0.5">
                    <Sparkles className="h-3.5 w-3.5 text-amber-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs font-semibold text-zinc-100">{item.title}</div>
                      <span className="text-[10px] text-amber-400 bg-amber-950/60 border border-amber-800/40 px-2 py-0.5 rounded-full font-medium">
                        {item.badge}
                      </span>
                    </div>
                    <p className="text-xs text-zinc-400 mt-1 leading-relaxed">{item.body}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          )}

          {/* TAB 5: POS SYNC */}
          {activeTab === "pos" && (
            <motion.div
              key="pos"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-zinc-900/60 border border-zinc-800 p-4 rounded-xl flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-red-950/50 border border-red-800/50 grid place-items-center text-red-400 font-bold text-sm">
                      LS
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-white">Lightspeed Retail</div>
                      <div className="text-[11px] text-emerald-400 flex items-center gap-1 mt-0.5">
                        <CheckCircle2 className="h-3 w-3" /> Auto-sync active · 4m ago
                      </div>
                    </div>
                  </div>
                  <span className="text-xs text-zinc-400 font-mono tnum">1,482 txns/wk</span>
                </div>

                <div className="bg-zinc-900/60 border border-zinc-800 p-4 rounded-xl flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-blue-950/50 border border-blue-800/50 grid place-items-center text-blue-400 font-bold text-sm">
                      SQ
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-white">Square Terminal</div>
                      <div className="text-[11px] text-emerald-400 flex items-center gap-1 mt-0.5">
                        <CheckCircle2 className="h-3 w-3" /> Auto-sync active · 2m ago
                      </div>
                    </div>
                  </div>
                  <span className="text-xs text-zinc-400 font-mono tnum">894 txns/wk</span>
                </div>
              </div>

              <div className="bg-zinc-900/40 border border-zinc-800/80 rounded-xl p-3.5 flex items-center justify-between text-xs">
                <div className="flex items-center gap-2 text-zinc-300">
                  <ShieldCheck className="h-4 w-4 text-emerald-400" />
                  <span>Encrypted POS Tokens · Automatic Daily Transaction Reconciliation</span>
                </div>
                <span className="text-zinc-500">Zero duplicate entries</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
